import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { format, subDays, subMonths } from "date-fns";
import { it } from "date-fns/locale";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";
import { Calendar as CalendarIcon, ChevronDown, LogOut, Plus, Star, TrendingUp, Award, Store, User as UserIcon, TrendingDown, Sparkles as SparklesIcon, Download } from "lucide-react";
import { Link } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";
import PlanBadge from "@/components/PlanBadge";
import AiInsightsSection from "@/components/AiInsightsSection";
import AiReplyDialog from "@/components/AiReplyDialog";
import OnboardingWizard from "@/components/OnboardingWizard";
import TrialBanner from "@/components/TrialBanner";
import AdvancedAnalyticsSection from "@/components/AdvancedAnalyticsSection";
import ExportDialog from "@/components/ExportDialog";
import MobileAccountMenu from "@/components/MobileAccountMenu";
import { DASH, AUTH } from "@/constants/testIds/app";

const CHART_COLORS = {
  5: "#0F5132",
  4: "#4A8A6E",
  3: "#E9A94E",
  2: "#E86A3B",
  1: "#C6423B",
};

const PRESETS = [
  { key: "30d", label: "Ultimi 30 giorni", days: 30, testId: DASH.datePreset30d },
  { key: "90d", label: "Ultimi 3 mesi", days: 90, testId: DASH.datePreset90d },
  { key: "6m", label: "Ultimi 6 mesi", days: 182, testId: DASH.datePreset6m },
  { key: "1y", label: "Ultimo anno", days: 365, testId: DASH.datePreset1y },
];

function Avatar({ name }) {
  const initials = name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?";
  return (
    <div className="h-9 w-9 rounded-full bg-[#00F2F4] text-[#0A0A0A] flex items-center justify-center font-sans text-xs font-semibold">
      {initials}
    </div>
  );
}

function StarRow({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          className={i <= rating ? "fill-[#E9A94E] text-[#E9A94E]" : "text-[var(--border-c)]"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, testId, delta, deltaTestId, deltaSuffix }) {
  const showDelta = delta !== undefined && delta !== null && !Number.isNaN(delta);
  const positive = showDelta && delta > 0;
  const negative = showDelta && delta < 0;
  return (
    <motion.div
      initial={{ y: 16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="p-6 bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl min-h-[140px] flex flex-col justify-between shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">{label}</div>
        <div className="h-9 w-9 rounded-full bg-[var(--bg-3)] flex items-center justify-center text-[var(--fg-2)]">
          <Icon size={16} strokeWidth={1.7} />
        </div>
      </div>
      <div>
        <div className="flex items-baseline gap-3">
          <div className="font-serif text-4xl text-[var(--fg-1)] tracking-tight leading-none">{value}</div>
          {showDelta && (
            <div
              data-testid={deltaTestId}
              className={`inline-flex items-center gap-1 text-xs font-sans font-semibold px-2 py-0.5 rounded-full ${
                positive
                  ? "bg-[#EEF2ED] text-[#00F2F4]"
                  : negative
                  ? "bg-[#F7ECEA] text-[#B94A48]"
                  : "bg-[var(--bg-3)] text-[var(--fg-3)]"
              }`}
            >
              {positive ? <TrendingUp size={11} /> : negative ? <TrendingDown size={11} /> : null}
              {delta > 0 ? "+" : ""}
              {delta}
              {deltaSuffix || ""}
            </div>
          )}
        </div>
        {sub && <div className="mt-2 text-xs text-[var(--fg-3)]">{sub}</div>}
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [presetKey, setPresetKey] = useState("30d");
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newBizName, setNewBizName] = useState("");
  const [newBizAddr, setNewBizAddr] = useState("");
  const [isPro, setIsPro] = useState(false);
  const [aiReplyReview, setAiReplyReview] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  // Load plan status for AI Spark (button visibility)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plan/status");
        setIsPro(data?.effective_plan === "pro");
      } catch {
        setIsPro(false);
      }
    })();
  }, []);

  // Load businesses
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/businesses");
        setBusinesses(data);
        if (data.length > 0) setSelectedBiz(data[0].business_id);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Load stats & reviews when biz or range changes
  useEffect(() => {
    if (!selectedBiz) return;
    setLoading(true);
    const start = dateRange.from ? new Date(dateRange.from).toISOString() : undefined;
    const endDate = dateRange.to ? new Date(dateRange.to) : new Date();
    endDate.setHours(23, 59, 59, 999);
    const end = endDate.toISOString();

    Promise.all([
      api.get("/reviews/stats", { params: { business_id: selectedBiz, start, end, compare: true } }),
      api.get("/reviews", { params: { business_id: selectedBiz, start, end, limit: 20 } }),
    ])
      .then(([s, r]) => {
        setStats(s.data);
        setReviews(r.data);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [selectedBiz, dateRange]);

  const applyPreset = (preset) => {
    setPresetKey(preset.key);
    setDateRange({ from: subDays(new Date(), preset.days), to: new Date() });
    setPopoverOpen(false);
  };

  const createBusiness = async () => {
    if (!newBizName.trim()) {
      toast.error("Inserisci un nome per l'attività");
      return;
    }
    try {
      const { data } = await api.post("/businesses", { name: newBizName, address: newBizAddr });
      setBusinesses((prev) => [...prev, data]);
      setSelectedBiz(data.business_id);
      setDialogOpen(false);
      setNewBizName("");
      setNewBizAddr("");
      toast.success("Attività creata");
    } catch {
      toast.error("Errore durante la creazione");
    }
  };

  const pieData = useMemo(() => {
    if (!stats) return [];
    return stats.distribution
      .filter((d) => d.count > 0)
      .map((d) => ({
        name: `${d.stars} Stelle`,
        stars: d.stars,
        value: d.count,
        percentage: d.percentage,
      }))
      .sort((a, b) => b.stars - a.stars);
  }, [stats]);

  const rangeLabel = useMemo(() => {
    if (!dateRange.from) return "Seleziona periodo";
    const from = format(dateRange.from, "d MMM yyyy", { locale: it });
    const to = format(dateRange.to || new Date(), "d MMM yyyy", { locale: it });
    return `${from} — ${to}`;
  }, [dateRange]);

  return (
    <div className="min-h-screen bg-[var(--bg-1)]" data-testid={DASH.container}>
      <Toaster position="top-right" theme={theme} />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[var(--glass-bg)] backdrop-blur-xl border-b border-[var(--border-c)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="font-serif text-3xl text-[var(--fg-1)] tracking-tight leading-none">
              Recen<span className="italic text-[#00F2F4]">Up</span>
            </div>
            <PlanBadge className="hidden md:inline-flex" />
            <div className="hidden md:flex items-center gap-3 text-xs text-[var(--fg-3)] uppercase tracking-[0.2em]">
              <div className="h-4 w-px bg-[var(--border-c)]" />
              Analytics recensioni
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Business switcher */}
            <div className="hidden md:flex items-center gap-2">
              <span className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                Attività
              </span>
              <Select value={selectedBiz || ""} onValueChange={setSelectedBiz}>
                <SelectTrigger
                  data-testid={DASH.businessSelector}
                  className="min-w-[200px] rounded-full border-[var(--border-c)] bg-[var(--bg-2)] font-sans text-sm"
                >
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map((b) => (
                    <SelectItem
                      key={b.business_id}
                      value={b.business_id}
                      data-testid={DASH.businessSelectorItem(b.business_id)}
                    >
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    data-testid={DASH.addBusinessBtn}
                    className="h-9 w-9 rounded-full bg-[var(--bg-3)] hover:bg-[var(--border-c)] flex items-center justify-center text-[var(--fg-2)]"
                    style={{ transition: "background-color 0.2s ease" }}
                    title="Aggiungi attività"
                  >
                    <Plus size={16} />
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[var(--bg-1)]">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Nuova attività</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <Input
                      data-testid={DASH.newBusinessNameInput}
                      placeholder="Nome (es. Trattoria da Luigi)"
                      value={newBizName}
                      onChange={(e) => setNewBizName(e.target.value)}
                    />
                    <Input
                      data-testid={DASH.newBusinessAddressInput}
                      placeholder="Indirizzo (opzionale)"
                      value={newBizAddr}
                      onChange={(e) => setNewBizAddr(e.target.value)}
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      data-testid={DASH.saveBusinessBtn}
                      onClick={createBusiness}
                      className="rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)]"
                    >
                      Crea
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Theme toggle */}
            <ThemeToggle />

            {/* User + logout */}
            <div className="flex items-center gap-3 pl-3 border-l border-[var(--border-c)]">
              <Link
                to="/assistenza"
                data-testid="dashboard-help-nav"
                className="hidden md:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-sans font-semibold text-[var(--fg-2)] hover:text-[#00F2F4] px-2.5 py-1 rounded-full border border-[var(--border-c)] hover:border-[#00F2F4]/40"
                style={{ transition: "color 0.2s ease, border-color 0.2s ease" }}
                title="Centro Assistenza"
              >
                Assistenza
              </Link>
              {user?.is_admin && (
                <Link
                  to="/admin/card-requests"
                  data-testid="admin-nav-link"
                  className="hidden md:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-sans font-semibold text-[#00F2F4] hover:text-[#00D6D8] px-2.5 py-1 rounded-full border border-[#00F2F4]/30"
                  style={{ transition: "color 0.2s ease, border-color 0.2s ease" }}
                  title="Area admin"
                >
                  Admin
                </Link>
              )}
              <Link
                to="/profile"
                data-testid={DASH.profileLink}
                className="h-9 w-9 rounded-full hover:bg-[var(--bg-3)] hidden md:flex items-center justify-center text-[var(--fg-2)]"
                style={{ transition: "background-color 0.2s ease" }}
                title="Profilo"
              >
                <UserIcon size={16} />
              </Link>
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
              ) : (
                <Avatar name={user?.name} />
              )}
              <button
                data-testid={AUTH.logoutBtn}
                onClick={logout}
                className="h-9 w-9 rounded-full hover:bg-[var(--bg-3)] hidden md:flex items-center justify-center text-[var(--fg-2)]"
                style={{ transition: "background-color 0.2s ease" }}
                title="Esci"
              >
                <LogOut size={16} />
              </button>
              <MobileAccountMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 space-y-8">
        {/* Panoramica — Title + date range */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-[10px] font-sans uppercase tracking-[0.25em] text-[#00F2F4] font-semibold">
              Dashboard
            </div>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl text-[var(--fg-1)] tracking-tight">
              {businesses.find((b) => b.business_id === selectedBiz)?.name || "Panoramica"}
            </h1>
            <p className="mt-2 text-sm text-[var(--fg-3)]">
              {businesses.find((b) => b.business_id === selectedBiz)?.address || "Analisi delle recensioni ricevute nel periodo selezionato"}
            </p>
          </div>

          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                data-testid={DASH.dateRangeTrigger}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] bg-[var(--bg-2)] px-5 py-3 text-sm text-[var(--fg-1)] font-sans hover:border-[var(--fg-2)] focus:ring-2 focus:ring-[var(--fg-1)] focus:outline-none"
                style={{ transition: "border-color 0.2s ease" }}
              >
                <CalendarIcon size={16} className="text-[var(--fg-3)]" />
                {rangeLabel}
                <ChevronDown size={14} className="text-[var(--fg-3)]" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="p-0 w-auto bg-[var(--bg-2)] border-[var(--border-c)]">
              <div className="flex">
                <div className="w-48 border-r border-[var(--border-c)] p-3 flex flex-col gap-1">
                  <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)] px-3 py-2">
                    Preset
                  </div>
                  {PRESETS.map((p) => (
                    <button
                      key={p.key}
                      data-testid={p.testId}
                      onClick={() => applyPreset(p)}
                      className={`text-left px-3 py-2 rounded-md text-sm font-sans ${
                        presetKey === p.key
                          ? "bg-[var(--bg-3)] text-[var(--fg-1)]"
                          : "text-[var(--fg-2)] hover:bg-[var(--bg-1)]"
                      }`}
                      style={{ transition: "background-color 0.15s ease" }}
                    >
                      {p.label}
                    </button>
                  ))}
                  <div className="px-3 py-2 text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                    Personalizzato
                  </div>
                  <div data-testid={DASH.datePresetCustom} className="px-3 text-xs text-[var(--fg-3)]">
                    Seleziona sul calendario →
                  </div>
                </div>
                <div className="p-3">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(r) => {
                      if (r?.from) {
                        setDateRange(r);
                        setPresetKey("custom");
                      }
                    }}
                    numberOfMonths={2}
                    locale={it}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* AI Insights (Pro-only, server-gated) */}
        <AiInsightsSection businessId={selectedBiz} dateRange={dateRange} presetKey={presetKey} />

        {/* Advanced Analytics (Pro-only, server-gated) */}
        {isPro && (
          <>
            <AdvancedAnalyticsSection businessId={selectedBiz} dateRange={dateRange} />
            <div className="flex justify-end">
              <button
                onClick={() => setExportOpen(true)}
                data-testid="dashboard-export-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[#00F2F4] text-[var(--fg-1)] px-4 py-2 text-sm font-sans"
                style={{ transition: "border-color 0.2s ease" }}
              >
                <Download size={14} /> Esporta report
              </button>
            </div>
          </>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <KpiCard
            testId={DASH.kpiTotal}
            deltaTestId={DASH.kpiTotalDelta}
            label="Totale recensioni"
            value={stats?.total ?? "—"}
            sub={
              stats?.comparison
                ? "vs periodo precedente"
                : "nel periodo selezionato"
            }
            icon={TrendingUp}
            delta={stats?.comparison?.delta_total_pct}
            deltaSuffix="%"
          />
          <KpiCard
            testId={DASH.kpiAverage}
            deltaTestId={DASH.kpiAverageDelta}
            label="Media stelle"
            value={stats?.average ? stats.average.toFixed(2) : "—"}
            sub={
              stats?.comparison
                ? "vs periodo precedente"
                : "valutazione media"
            }
            icon={Star}
            delta={stats?.comparison?.delta_average}
            deltaSuffix=" ★"
          />
          <KpiCard
            testId={DASH.kpiMostCommon}
            label="Voto più frequente"
            value={stats?.most_common_rating ? `${stats.most_common_rating}★` : "—"}
            sub="rating più comune"
            icon={Award}
          />
        </div>

        {/* Chart + Reviews */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Pie chart */}
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 p-8 bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
            data-testid={DASH.pieChart}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Distribuzione
                </div>
                <h3 className="mt-1 font-serif text-2xl text-[var(--fg-1)] tracking-tight">
                  Recensioni per stelle
                </h3>
              </div>
            </div>

            {stats?.total > 0 ? (
              <>
                <div className="relative h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={95}
                        paddingAngle={2}
                        dataKey="value"
                        stroke="none"
                      >
                        {pieData.map((entry) => (
                          <Cell key={entry.stars} fill={CHART_COLORS[entry.stars]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "#fff",
                          border: "1px solid var(--border-c)",
                          borderRadius: 8,
                          fontSize: 12,
                          fontFamily: "Manrope, sans-serif",
                        }}
                        formatter={(value, name, props) => [
                          `${value} (${props.payload.percentage}%)`,
                          name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="font-serif text-4xl text-[var(--fg-1)] leading-none">
                      {stats.average.toFixed(1)}
                    </div>
                    <div className="mt-1 text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                      Media
                    </div>
                  </div>
                </div>

                {/* Custom legend */}
                <div className="mt-6 grid grid-cols-1 gap-2">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const item = stats.distribution.find((d) => d.stars === star);
                    return (
                      <div key={star} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-3">
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ background: CHART_COLORS[star] }}
                          />
                          <span className="font-sans text-[var(--fg-2)]">{star} Stelle</span>
                        </div>
                        <div className="font-sans text-[var(--fg-3)]">
                          <span className="text-[var(--fg-1)] font-semibold">{item?.count || 0}</span>
                          <span className="mx-2 text-[var(--fg-4)]">·</span>
                          {item?.percentage || 0}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="h-64 flex items-center justify-center text-sm text-[var(--fg-3)]">
                {loading ? "Caricamento..." : "Nessuna recensione nel periodo selezionato"}
              </div>
            )}
          </motion.div>

          {/* Reviews list */}
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-3 p-8 bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
            data-testid={DASH.reviewsList}
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Ultime recensioni
                </div>
                <h3 className="mt-1 font-serif text-2xl text-[var(--fg-1)] tracking-tight">
                  Voci dei clienti
                </h3>
              </div>
              <div className="text-xs text-[var(--fg-3)] font-sans">
                {reviews.length} mostrate
              </div>
            </div>

            <div className="mt-4 divide-y divide-[var(--border-c)]">
              {reviews.length === 0 && !loading && (
                <div className="py-12 text-center text-sm text-[var(--fg-3)]">
                  <Store className="mx-auto mb-3 text-[var(--fg-4)]" size={28} strokeWidth={1.4} />
                  Nessuna recensione nel periodo selezionato.
                </div>
              )}
              {reviews.map((r) => (
                <div
                  key={r.review_id}
                  data-testid={DASH.reviewItem(r.review_id)}
                  className="py-5 flex items-start gap-4"
                >
                  <Avatar name={r.author_name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-sans font-semibold text-sm text-[var(--fg-1)] truncate">
                        {r.author_name}
                      </div>
                      <div className="text-xs text-[var(--fg-4)] font-sans whitespace-nowrap">
                        {format(new Date(r.created_at), "d MMM yyyy", { locale: it })}
                      </div>
                    </div>
                    <div className="mt-1">
                      <StarRow rating={r.rating} />
                    </div>
                    <p className="mt-2 text-sm text-[var(--fg-2)] leading-relaxed">{r.text}</p>
                    {isPro && (
                      <button
                        type="button"
                        onClick={() => setAiReplyReview(r)}
                        data-testid={`ai-reply-review-btn-${r.review_id}`}
                        className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-sans font-semibold text-[#00F2F4] hover:text-[#00D6D8] border border-[#00F2F4]/30 hover:border-[#00F2F4] px-2.5 py-1 rounded-full"
                        style={{ transition: "color 0.2s ease, border-color 0.2s ease" }}
                        title="Suggerisci risposta AI"
                      >
                        <SparklesIcon size={11} strokeWidth={2.2} /> Rispondi con AI
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </main>

      {/* Fase 6 Part B — AI Spark dialog */}
      <AiReplyDialog
        review={aiReplyReview}
        open={!!aiReplyReview}
        onOpenChange={(o) => { if (!o) setAiReplyReview(null); }}
      />

      {/* Sprint 8 — Onboarding + Trial banner */}
      <TrialBanner />
      <OnboardingWizard />
    </div>
  );
}
